import { createStore, del, get, set } from 'idb-keyval'
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister'
import type { Query } from '@tanstack/react-query'

// Dedicated IDB store to avoid conflicts with AT Protocol OAuth databases
const idbStore = createStore('mackinac-rq-cache', 'queryCache')

// 24 hours — restored queries need enough gcTime to survive until refetch
export const PERSIST_GC_TIME = 24 * 60 * 60 * 1000

// Must match the key used by createAsyncStoragePersister (default: 'REACT_QUERY_OFFLINE_CACHE')
const PERSIST_CACHE_KEY = 'REACT_QUERY_OFFLINE_CACHE'

const PERSISTABLE_PREFIXES = [
    'timeline',
    'posts',
    'notifications',
    'thread',
    // 'viewProfile' is intentionally excluded: profile data changes frequently and users
    // expect updates (display name, bio, avatar) to appear immediately. Persisting to
    // IndexedDB causes stale data to be served across hard refreshes since React Query
    // treats restored data as fresh within the staleTime window.
    'profileSummary',
    'reposts',
    // 'profileBatch' is intentionally excluded: the query returns a Map<string, ProfileBasic>,
    // which serializes to {} in JSON. Persisting it would restore a broken plain object that
    // causes "authorProfiles?.get is not a function" crashes in useArticlesFeed.
]

/**
 * Only persist successful queries for known data prefixes.
 * Keeps the IDB footprint small and avoids caching errors or transient states.
 */
export function shouldDehydrateQuery(query: Query): boolean {
    if (query.state.status !== 'success') return false
    const firstKey = query.queryKey[0]
    if (typeof firstKey !== 'string') return false
    return PERSISTABLE_PREFIXES.includes(firstKey)
}

export const asyncStoragePersister = createAsyncStoragePersister({
    key: PERSIST_CACHE_KEY,
    storage: {
        getItem: async (key: string) => {
            const value = await get<string>(key, idbStore)
            return value ?? null
        },
        setItem: async (key: string, value: string) => {
            await set(key, value, idbStore)
        },
        removeItem: async (key: string) => {
            await del(key, idbStore)
        },
    },
})

/** Clear persisted cache on logout to prevent cross-user data leaks */
export async function clearPersistedCache(): Promise<void> {
    try {
        await del(PERSIST_CACHE_KEY, idbStore)
    } catch {
        // Ignore — IDB may be unavailable in some contexts
    }
}
